import {
  KmxBadgeCheckFilled,
  KmxExclamationContainerCircleFilled,
  KmxInfoCircleFilled,
} from '@kmx/mui-icons';
import { styled } from '@mui/material/styles';

import {
  FbsUnavailable,
  HighDownPaymentBody,
  HighDownPaymentDisclaimer,
  InformationUnavailableBody,
  PrequalificationDisclaimer,
} from '../components/Finance/TermsModalContent/FinanceTermsModalContent';
import DECISIONTYPES from '../constants/fbsDecisionLabelTypeConstants';
import type { FbsDecision } from '../types/FbsDecision';
import { formatCurrency } from './formatters';

type ModalContentProps = {
  fbsDecision: FbsDecision;
  downPayment: number;
};

type ModalContentReturnType = {
  hasTerms: boolean;
  icon: React.ReactNode;
  type: string;
  title: string | React.ReactNode;
  body?: React.ReactNode;
  disclaimerText?: React.ReactNode;
};

const StyledSup = styled('sup')(() => ({ fontSize: '0.6em' }));

export default {
  approved: ({
    fbsDecision,
    downPayment,
  }: ModalContentProps): ModalContentReturnType => {
    const carMonthlyPayment =
      fbsDecision?.terms[0]?.maxTermRate?.monthlyPayment || 0;

    if (carMonthlyPayment === 0) {
      return {
        hasTerms: false,
        icon: <KmxInfoCircleFilled />,
        type: DECISIONTYPES.notNeeded,
        title: 'Purchase with your down payment',
        body: <HighDownPaymentBody />,
        disclaimerText: <HighDownPaymentDisclaimer />,
      };
    }

    return {
      hasTerms: true,
      icon: <KmxBadgeCheckFilled />,
      type: DECISIONTYPES.preApproved,
      title: (
        <span>
          Pre-qualified with &nbsp;
          {formatCurrency(fbsDecision.conditionAmount + downPayment)} down
          <StyledSup>‡</StyledSup>
        </span>
      ),
      body: undefined,
      disclaimerText: <PrequalificationDisclaimer />,
    };
  },

  countered: ({ fbsDecision }: ModalContentProps): ModalContentReturnType => {
    /* TODO: This is a temporary way of determining vehicles where pre-approval is unavailable.
      FBS will be making a change to determine it on their side and pass a different status instead.
      10/14/2025 - Checked with Josh Bradley and these are still needed for now
    */
    if (
      fbsDecision &&
      fbsDecision.lenderName === 'Cash' &&
      fbsDecision.totalFinanceAmount === 0
    ) {
      return {
        hasTerms: false,
        icon: <KmxInfoCircleFilled />,
        type: DECISIONTYPES.declined,
        title: 'Pre-qualification unavailable',
        body: <FbsUnavailable />,
        disclaimerText: undefined,
      };
    }
    return {
      hasTerms: true,
      icon: <KmxExclamationContainerCircleFilled />,
      type: DECISIONTYPES.additionalDown,
      title: (
        <span>
          Pre-qualified with an additional &nbsp;
          {formatCurrency(fbsDecision.conditionAmount)} down
          <StyledSup>‡</StyledSup>
        </span>
      ),
      body: undefined,
      disclaimerText: <PrequalificationDisclaimer />,
    };
  },

  declined: (): ModalContentReturnType => {
    return {
      hasTerms: false,
      icon: <KmxInfoCircleFilled />,
      type: DECISIONTYPES.declined,
      title: 'Pre-qualification unavailable',
      body: <FbsUnavailable />,
      disclaimerText: undefined,
    };
  },

  default: (): ModalContentReturnType => ({
    hasTerms: false,
    icon: <KmxInfoCircleFilled />,
    type: DECISIONTYPES.noInfo,
    title: 'Information unavailable',
    body: <InformationUnavailableBody />,
    disclaimerText: undefined,
  }),
};
